import { useSyncExternalStore } from "react";

/**
 * The open lip's height on desktop, which the user sets by dragging its handle. It lives in one CSS
 * variable on the root, --assistant-lip-size, which the lip's height and --assistant-lip-cover
 * read; unset, both fall back to --assistant-lip-height capped at 72vh. JS only writes the
 * variable: a drag writes it per pointermove, React hears of the height only once it is set.
 */

export const LIP_SIZE_KEY = "steddy:assistant-lip-height";
export const LIP_SIZE_VAR = "--assistant-lip-size";
export const LIP_MIN = 320;
/** Room left above a full-height lip: the environment ribbon and the page's title row. */
export const LIP_TOP_GAP = 64;
/** An open lip dragged this far below LIP_MIN closes on release. */
export const LIP_CLOSE_MARGIN = 80;
/** One Up or Down press on the handle. */
export const LIP_STEP = 40;
/** A stored height outside LIP_MIN..this was never one the lip had: the default instead. */
const STORED_MAX = 4000;
/** The default's share of the viewport (the 72vh in the lip's CSS). */
const DEFAULT_SHARE = 0.72;
/** --assistant-lip-height, for when the stylesheet cannot be read (tests). */
const TOKEN_FALLBACK = 540;

export function lipMax(viewport: number): number {
  return Math.max(LIP_MIN, viewport - LIP_TOP_GAP);
}

export function clampLipSize(px: number, viewport: number): number {
  return Math.round(Math.min(Math.max(px, LIP_MIN), lipMax(viewport)));
}

let token: number | undefined;

/** --assistant-lip-height, read once the stylesheet is in (renders read it, never per frame). */
function tokenHeight(): number {
  token ??=
    parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue("--assistant-lip-height")
    ) || undefined;
  return token ?? TOKEN_FALLBACK;
}

/** The height a lip with no chosen height has, as its CSS computes it. */
export function defaultLipSize(viewport: number): number {
  return clampLipSize(Math.min(tokenHeight(), viewport * DEFAULT_SHARE), viewport);
}

/** The stored height, or null for the default: missing, unreadable, blocked or out of range. */
export function readStoredLipSize(
  storage: () => Storage = () => window.localStorage
): number | null {
  try {
    const raw = storage().getItem(LIP_SIZE_KEY);
    const px = raw == null ? NaN : Number(raw);
    return Number.isFinite(px) && px >= LIP_MIN && px <= STORED_MAX ? px : null;
  } catch {
    return null;
  }
}

function storeLipSize(px: number | null): void {
  try {
    if (px == null) window.localStorage.removeItem(LIP_SIZE_KEY);
    else window.localStorage.setItem(LIP_SIZE_KEY, String(px));
  } catch {
    // Blocked storage: the height lasts for this page.
  }
}

/** What a released drag leaves: the height to open at, or null to close (back to peek). */
export function releasedLipSize(px: number, viewport: number): number | null {
  return px < LIP_MIN - LIP_CLOSE_MARGIN ? null : clampLipSize(px, viewport);
}

let chosen = readStoredLipSize();
const listeners = new Set<() => void>();

/** The open lip's height now: the chosen one, or the default, fitted to the viewport. */
export function lipSizeNow(viewport = window.innerHeight): number {
  return chosen == null ? defaultLipSize(viewport) : clampLipSize(chosen, viewport);
}

/** Writes the variable: `px` as given (a drag in progress), else the chosen height fitted now. */
export function applyLipSize(px?: number): void {
  const style = document.documentElement.style;
  if (px != null) style.setProperty(LIP_SIZE_VAR, `${Math.round(px)}px`);
  else if (chosen == null) style.removeProperty(LIP_SIZE_VAR);
  else style.setProperty(LIP_SIZE_VAR, `${clampLipSize(chosen, window.innerHeight)}px`);
}

/** Chooses the height (null: back to the default), stores it and applies it. */
export function setLipSize(px: number | null): void {
  chosen = px == null ? null : clampLipSize(px, window.innerHeight);
  storeLipSize(chosen);
  applyLipSize();
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The open lip's height and its largest, for the handle's aria values. */
export function useLipSize(): { now: number; max: number } {
  const now = useSyncExternalStore(subscribe, () => lipSizeNow());
  const max = useSyncExternalStore(subscribe, () => lipMax(window.innerHeight));
  return { now, max };
}

// Before the first paint: the chosen height is on the root before the lip renders, so it never
// flashes the default. A smaller window fits it again; the choice itself is kept.
applyLipSize();
window.addEventListener("resize", () => {
  applyLipSize();
  listeners.forEach((listener) => listener());
});
